import React from 'react';

interface ToggleSwitchProps {
  id: string;
  label: string;
  description?: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
  className?: string;
}

const ToggleSwitch: React.FC<ToggleSwitchProps> = ({
  id,
  label,
  description,
  checked,
  onChange,
  disabled = false,
  className = ''
}) => {
  const handleToggle = () => {
    if (!disabled) {
      onChange(!checked);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === ' ' || e.key === 'Enter') {
      e.preventDefault();
      handleToggle();
    }
  };

  return (
    <div className={`flex items-start justify-between ${className}`}>
      {/* Label and Description */}
      <div className="flex-1 mr-4">
        <label 
          htmlFor={id}
          className={`block text-sm font-bold cursor-pointer ${
            disabled 
              ? 'text-gray-400 dark:text-gray-500 cursor-not-allowed' 
              : 'text-gray-700 dark:text-gray-300'
          }`}
        >
          {label}
        </label>
        {description && (
          <p className={`text-sm mt-1 ${
            disabled 
              ? 'text-gray-400 dark:text-gray-500' 
              : 'text-gray-500 dark:text-gray-400'
          }`}>
            {description}
          </p>
        )}
      </div>

      {/* Toggle Switch */}
      <div
        role="switch"
        aria-checked={checked}
        aria-labelledby={id}
        tabIndex={disabled ? -1 : 0}
        onClick={handleToggle}
        onKeyDown={handleKeyDown}
        className={`
          relative inline-flex h-7 w-12 items-center rounded-full transition-colors duration-200
          focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 dark:focus:ring-offset-gray-800
          ${disabled
            ? 'opacity-50 cursor-not-allowed'
            : 'cursor-pointer'
          }
          ${checked
            ? 'bg-primary'
            : 'bg-gray-200 dark:bg-gray-600'
          }
        `}
      >
        {/* Toggle Button */}
        <span
          className={`
            inline-block h-5 w-5 transform rounded-full bg-white transition-all duration-200 ease-[cubic-bezier(0.34,1.56,0.64,1)]
            ${checked ? 'translate-x-6 shadow-md' : 'translate-x-1 shadow-sm'}
          `}
          aria-hidden="true"
        />
      </div>

      {/* Hidden input for form compatibility */}
      <input
        id={id}
        type="checkbox"
        checked={checked}
        onChange={() => {}} // Controlled by the toggle
        disabled={disabled}
        className="sr-only"
        aria-hidden="true"
      />
    </div>
  );
};

export default ToggleSwitch;